import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { FINANCIAL_ROUTES } from "@/utils/financialFlow";
import { useSingleInsuranceRecord, INITIAL_AUTO } from "@/hooks/useInsuranceRecord";
import {
  ERROR_INPUT_CLASS,
  FieldError,
  isValidPhone,
  phoneDigits,
  formatPhoneDisplay,
  cn,
} from "@/components/insurance/insuranceForm";
import type { InsuranceInfo } from "@/store/insurance";

type AutoForm = {
  carrier: string;
  carrierPhone: string;
  idNum: string;
  attName: string;
  attPhone: string;
  medPay: boolean;
  hasAttorney: boolean;
};

const EMPTY: AutoForm = {
  carrier: "",
  carrierPhone: "",
  idNum: "",
  attName: "",
  attPhone: "",
  medPay: false,
  hasAttorney: false,
};

export function AutoInsurancePage() {
  const navigate = useNavigate();
  const { loading, record, saveField, flush } = useSingleInsuranceRecord({
    initial: INITIAL_AUTO,
    select: (list: InsuranceInfo[]) => list.find((r) => Number(r.aa) === 1),
  });

  const [form, setForm] = useState<AutoForm>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof AutoForm, string>>>({});

  useEffect(() => {
    if (record) {
      setForm({
        carrier: record.carrier ?? "",
        carrierPhone: (record.carrierPhone ?? "").replace(/\D/g, ""),
        idNum: record.idNum ?? "",
        attName: record.attName ?? "",
        attPhone: (record.attPhone ?? "").replace(/\D/g, ""),
        // Backend may return medPay as number or string — accept both.
        medPay: record.medPay === 1 || String(record.medPay) === "1",
        hasAttorney: Boolean(record.attName || record.attPhone),
      });
    }
  }, [record]);

  const updateText = (field: "carrier" | "carrierPhone" | "idNum" | "attName" | "attPhone", value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
    saveField(field, value);
  };

  const toggleMedPay = (checked: boolean) => {
    setForm((f) => ({ ...f, medPay: checked }));
    saveField("medPay", checked ? "1" : "0");
  };

  const toggleAttorney = (checked: boolean) => {
    setForm((f) => ({ ...f, hasAttorney: checked }));
    if (!checked) setErrors((e) => ({ ...e, attName: undefined, attPhone: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof AutoForm, string>> = {};
    if (!form.carrier.trim()) next.carrier = "Insurance company name is required.";
    if (!form.carrierPhone.trim()) next.carrierPhone = "Phone is required.";
    else if (!isValidPhone(form.carrierPhone)) next.carrierPhone = "Enter a valid 10-digit phone.";
    if (!form.idNum.trim()) next.idNum = "Policy number is required.";
    if (form.hasAttorney) {
      if (!form.attName.trim()) next.attName = "Attorney name is required.";
      if (!form.attPhone.trim()) next.attPhone = "Attorney phone is required.";
      else if (!isValidPhone(form.attPhone)) next.attPhone = "Enter a valid 10-digit phone.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const goBack = async () => {
    await flush();
    navigate(FINANCIAL_ROUTES.q1);
  };
  const goToQ2 = async () => {
    await flush();
    navigate(FINANCIAL_ROUTES.q2);
  };
  const onContinue = async () => {
    if (validate()) await goToQ2();
  };

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content financial-responsibility-content flex justify-center px-4 py-6">
        <div className="complaint-history-card financial-step-card">
          <h2 className="mb-1 text-[20px] font-extrabold text-healthsource-dark">Auto Insurance</h2>
          <p className="mb-6 text-[13px] text-healthsource-gray">
            Please enter your auto insurance details. You can also skip this for now and provide it later.
          </p>

          {loading ? (
            <p className="py-8 text-center text-[13px] text-healthsource-gray">Loading…</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="sm:col-span-1">
                <Label htmlFor="auto-carrier" required>Insurance Company</Label>
                <Input
                  id="auto-carrier"
                  placeholder="Insurance Company"
                  value={form.carrier}
                  onChange={(e) => updateText("carrier", e.target.value)}
                  className={cn(errors.carrier && ERROR_INPUT_CLASS)}
                  aria-invalid={errors.carrier ? true : undefined}
                />
                <FieldError message={errors.carrier} />
              </div>

              <div className="sm:col-span-1">
                <Label htmlFor="auto-carrierPhone" required>Phone</Label>
                <Input
                  id="auto-carrierPhone"
                  type="tel"
                  inputMode="tel"
                  placeholder="(XXX) XXX-XXXX"
                  value={formatPhoneDisplay(form.carrierPhone)}
                  onChange={(e) => updateText("carrierPhone", phoneDigits(e.target.value))}
                  className={cn(errors.carrierPhone && ERROR_INPUT_CLASS)}
                  aria-invalid={errors.carrierPhone ? true : undefined}
                />
                <FieldError message={errors.carrierPhone} />
              </div>

              <div className="sm:col-span-1">
                <Label htmlFor="auto-idNum" required>Policy Number</Label>
                <Input
                  id="auto-idNum"
                  placeholder="Policy Number"
                  value={form.idNum}
                  onChange={(e) => updateText("idNum", e.target.value)}
                  className={cn(errors.idNum && ERROR_INPUT_CLASS)}
                  aria-invalid={errors.idNum ? true : undefined}
                />
                <FieldError message={errors.idNum} />
              </div>

              <label className="sm:col-span-3 flex min-h-11 cursor-pointer items-center gap-3 text-[15px] font-semibold text-healthsource-dark">
                <input
                  type="checkbox"
                  checked={form.hasAttorney}
                  onChange={(e) => toggleAttorney(e.target.checked)}
                  className="h-5 w-5 shrink-0 rounded border-slate-300 accent-healthsource-navy"
                />
                I have an attorney for this claim
              </label>

              {form.hasAttorney && (
                <>
                  <div className="sm:col-span-2">
                    <Label htmlFor="auto-attName" required>Attorney Name</Label>
                    <Input
                      id="auto-attName"
                      placeholder="Attorney Name"
                      value={form.attName}
                      onChange={(e) => updateText("attName", e.target.value)}
                      className={cn(errors.attName && ERROR_INPUT_CLASS)}
                      aria-invalid={errors.attName ? true : undefined}
                    />
                    <FieldError message={errors.attName} />
                  </div>
                  <div className="sm:col-span-1">
                    <Label htmlFor="auto-attPhone" required>Attorney Phone</Label>
                    <Input
                      id="auto-attPhone"
                      type="tel"
                      inputMode="tel"
                      placeholder="(XXX) XXX-XXXX"
                      value={formatPhoneDisplay(form.attPhone)}
                      onChange={(e) => updateText("attPhone", phoneDigits(e.target.value))}
                      className={cn(errors.attPhone && ERROR_INPUT_CLASS)}
                      aria-invalid={errors.attPhone ? true : undefined}
                    />
                    <FieldError message={errors.attPhone} />
                  </div>
                </>
              )}

              <label className="sm:col-span-3 flex min-h-11 cursor-pointer items-center gap-3 text-[15px] font-semibold text-healthsource-dark">
                <input
                  type="checkbox"
                  checked={form.medPay}
                  onChange={(e) => toggleMedPay(e.target.checked)}
                  className="h-5 w-5 shrink-0 rounded border-slate-300 accent-healthsource-navy"
                />
                I have medical payments (MedPay) coverage
              </label>
            </div>
          )}
        </div>
      </div>

      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack}>Back</Button>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={goToQ2}>Skip for now</Button>
          <Button type="button" variant="primary" onClick={onContinue}>Continue</Button>
        </div>
      </WizardActionsBar>
    </div>
  );
}
